import React from "react";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CardHeader from "./CardHeader.jsx";
import { fullName } from "../../../utils/formatter.js";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { useRoles } from "../../../context/AuthenticationTokenContext";
import { isGrantedAny, role } from "shared/src/roles.mjs";

function ProblemInfo({ problem }) {
  if (!problem) return null;
  return (
    <Typography variant="body1">
      <strong>Problémový klient</strong>
      <br />
      {problem}
    </Typography>
  );
}

function buildInfoListItems(client, perms) {
  const c = client ?? {};
  const prefs = c.prefs ?? {};
  const requires_czech_speaker = !!prefs.requires_czech_speaker;
  const text_preference = prefs.text_preference ?? null;
  const any_doctor = !!c.any_doctor;
  const doctors = Array.isArray(c.doctors) ? c.doctors : [];

  const items = [];

  if (perms.canSeeRequiresCzechSpeaker && requires_czech_speaker) {
    items.push("Česky mluvící personál");
  }

  if (perms.canSeeDoctorNecessary) {
    if (any_doctor) {
      items.push("Vždy nutná přítomnost lékaře");
    } else if (doctors.length > 0) {
      items.push("Vždy nutná přítomnost lékaře");

      if (perms.canSeePreferredDoctor) {
        const doctorsStr = doctors
          .map((doc) => fullName(doc))
          .filter((d) => d != null && d !== "")
          .join(", ");
        if (doctorsStr)
          items.push(
            <Typography variant="body1" sx={{ mt: 1 }} component="div">
              <strong>Preferovaný lékař:</strong> {doctorsStr}
            </Typography>,
          );
      }
    }
  }

  if (perms.canSeeTextPreference && text_preference) {
    items.push(
      <Box sx={{ mt: 3 }}>
        <strong>Textová preference</strong>
        <br />
        {text_preference}
      </Box>,
    );
  }

  return items;
}

function InfoList({ items }) {
  const normalizedItems = Array.isArray(items) ? items : [];
  if (normalizedItems.length === 0) return null;

  return (
    <Typography variant="body1" component="div">
      <strong>Preference klienta</strong>
      {normalizedItems.map((item, i) => (
        <Box key={i}>{item}</Box>
      ))}
    </Typography>
  );
}

function ImportantInfo({ importantInfoItems }) {
  if (importantInfoItems.length === 0) {
    return;
  }

  return (
    <Typography variant="body1">
      <strong>Důležité info</strong>
      <br />
      {importantInfoItems.join(", ")}
    </Typography>
  );
}

export function PrefCard({ client, displayMinimalInfo = false }) {
  const theme = useTheme();
  const roles = useRoles();

  const perms = {
    canSeeRequiresCzechSpeaker: isGrantedAny(
      roles,
      new Set([
        role.Floor_Manager,
        role.Back_Office,
        role.Front_Office,
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    ),
    canSeeDoctorNecessary: isGrantedAny(
      roles,
      new Set([
        role.Floor_Manager,
        role.Back_Office,
        role.Front_Office,
        role.Dental_Assistant,
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    ),
    canSeePreferredDoctor: isGrantedAny(
      roles,
      new Set([
        role.Floor_Manager,
        role.Back_Office,
        role.Front_Office,
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    ),
    canSeeTextPreference: isGrantedAny(
      roles,
      new Set([
        role.Floor_Manager,
        role.Back_Office,
        role.Front_Office,
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    ),
  };

  const prefs = client?.prefs ?? {};
  const problem = prefs.problem ?? null;
  const items = buildInfoListItems(client, perms);
  const importantInfoItems =
    client?.importantInfoTags?.map((t) => t.value) ?? [];

  if (!problem && items.length === 0 && importantInfoItems.length === 0)
    return null;

  return (
    <Card>
      <CardContent>
        <CardHeader
          title="Preference klienta (nebo naše)"
          color={theme.palette.error.main}
          faIcon={faCircleExclamation}
        />
        <Stack spacing={3} direction="column" sx={{ mb: 3 }}>
          {!displayMinimalInfo && (
            <>
              <ImportantInfo importantInfoItems={importantInfoItems} />
              <ProblemInfo problem={problem} />
              <InfoList items={items} />
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
